import { useContext, useState, useEffect, useCallback } from 'react'

// api references
import { apiClient } from 'utils/api/apiClient'

// Soft UI Dashboard PRO React components
import DashboardLayout from 'components/LayoutContainers/DashboardLayout'

// Settings page components
import { StickyNotes } from './components/StickyNotes'

// Data
import { selectData } from 'layouts/onboarding'
import { IntakeContext } from 'context/IntakeContext'

// Popup
import { toast } from 'react-toastify'
import { useFormDebounce } from 'hooks/useFormDebounce'

import {
  format,
  differenceInYears,
  subYears,
  addMonths,
  differenceInMonths,
} from 'date-fns'
import { Box, Button, Typography, Grid } from '@mui/material'
import { callZapier } from 'utils/zapier/callZapier'
import { useApi } from 'hooks/useApi'

import { useDashboardCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useDashboardCalculations'
import { useRetirementGapCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useRetirementGapCalculations'
import { useFegliCalculations } from 'layouts/authenticated/pages/dashboard/hooks/useFegliCalculations'
import { usePARCOScoreCalculations } from 'layouts/authenticated/pages/dashboard/hooks/usePARCOScoreCalculations'
import { useAutoUpdateScores } from 'layouts/authenticated/pages/dashboard/hooks/useAutoUpdateScores'
import QuickNav from './components/QuickNav'
import ValuePoint from './components/ValuePoint'
import { Check, ContentCopy, Flag } from '@mui/icons-material'
import { BalanceSheetRow } from 'layouts/authenticated/pages/dashboard/components/BalanceSheet/BalanceSheetRow'
import { BalanceSheetModalForm } from 'layouts/authenticated/pages/dashboard'
import { UpdatedCalcs } from 'admin/UpdatedCalcs'
import TaskRecommendations from 'layouts/authenticated/pages/dashboard/components/TaskRecommendations'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import UserGoals from './components/UserGoals'
import FamilyDependents from './components/FamilyDependents'
import PrivateLifeInsurance from './components/PrivateLifeInsurance'
import QuickReference from './layouts/masterinputs/components/QuickReference'
import BasicInfo from './layouts/masterinputs/components/BasicInfo'
import Employment from './layouts/masterinputs/components/Employment'
import Military from './layouts/masterinputs/components/Military'
import Family from './layouts/masterinputs/components/Family'
import LeaveAndEarnings from './layouts/masterinputs/components/LeaveAndEarnings'
import TSPContributions from './layouts/masterinputs/components/TSPContributions'
import Healthcare from './layouts/masterinputs/components/Healthcare'
import Deductions from './layouts/masterinputs/components/Deductions'
import Fegli from './layouts/masterinputs/components/Fegli'
import SurvivorBenefit from './layouts/masterinputs/components/SurvivorBenefit'
import Union from './layouts/masterinputs/components/Union'
import BalanceSheet from './layouts/masterinputs/components/BalanceSheet'
import Goals from './layouts/masterinputs/components/Goals'
import SocialSecurity from './layouts/masterinputs/components/SocialSecurity'
import Taxes from './layouts/masterinputs/components/Taxes'
import Budgeting from './layouts/masterinputs/components/Budgeting'
import TSPAllocation from './layouts/masterinputs/components/TSPAllocation'
import IncomeStatement from './layouts/masterinputs/components/IncomeStatement'
import ToDoList from './layouts/masterinputs/components/ToDoList'
import Other from './layouts/masterinputs/components/Other'
import LoadingButton from 'components/LoadingButton'
import FinancialFrameworkCreator from './layouts/masterinputs/components/FinancialFrameworkCreator'
import AnnuityApplicationCreator from './layouts/masterinputs/components/AnnuityApplicationCreator'
import ProductBank from './layouts/masterinputs/components/ProductBank'
import Contributions401k from './layouts/masterinputs/components/401kContributions'
import AdminScheduleAppointment from './layouts/masterinputs/components/AdminScheduleAppointment'

const MasterInputs = () => {
  const {
    intakeState,
    updateIntakeStateAtKey,
    updateUser,
    userTasks,
    currentErrors,
    isAdmin,
    adminDetails,
  } = useContext(IntakeContext)
  const { apiRequest } = useApi()

  // console.log(intakeState)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()
  const isGovernmentEmployee = intakeState?.governmentEmployee
  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your user profile', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }

  const [ageQuestion, setAgeQuestion] = useState(true)

  const dateToYears = (startDate, endDate) => {
    const years =
      startDate && endDate
        ? differenceInYears(new Date(endDate), new Date(startDate))
        : ''
    const diff =
      startDate && endDate
        ? differenceInMonths(new Date(endDate), new Date(startDate))
        : ''
    const months =
      startDate && endDate
        ? differenceInMonths(
            new Date(endDate),
            addMonths(new Date(startDate), years * 12)
          )
        : ''
    return { years, months }
  }
  const yearsToDate = (years) =>
    format(subYears(new Date(), years), 'yyyy-MM-dd')

  const userNameTitle =
    intakeState?.firstName && intakeState?.firstName
      ? intakeState?.firstName + ' ' + intakeState?.lastName
      : 'Missing Name'
  const showSurvivorBenefit = intakeState?.familyMaritalStatus === 'married'

  const [localForm, setLocalForm] = useState({
    pitchType: 'aggressive_income',
    objective1: '',
    objective2: '',
    product1: '',
    product2: '',
    estimatedMonthlyExpenses: '',
    incomeTerm: 'monthly',
    annuityAmountA: 0,
    annuityAmountB: 0,
    annuity1AnnualIncomeA: 0,
    annuity1AnnualIncomeB: 0,
    annuity2AnnualIncomeA: 0,
    annuity2AnnualIncomeB: 0,
  })
  // console.log(localForm)
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }

  const sendToSalesforce = () => {
    const {
      createdAt,
      updatedAt,
      recommendations,
      documents,
      ...submittableValues
    } = intakeState
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/230xy7p/',
      payload: {
        user: intakeState,
      },
      sendNotification: false,
    })
    toast.success('You have successfully sent this client data to SalesForce', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }
  const updateSalesforce = () => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/2les9kh/',
      payload: {
        user: intakeState,
      },
      sendNotification: false,
    })
    toast.success('You have successfully sent this client data to SalesForce', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }
  //for use to update fields during automation
  const updateSalesforceTwo = () => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/2nrwxph/',
      payload: {
        user: intakeState,
      },
      sendNotification: false,
    })
    toast.success('You have successfully sent this client data to SalesForce', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }
  const sendICToSalesforce = () => {
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/2leiydz/',
      payload: {
        Event_ID: intakeState.salesforceICEventID,
        Event_Notes: intakeState.userNotes,
      },
      sendNotification: false,
    })
    toast.success(
      'You have successfully sent this client IC data to SalesForce',
      {
        position: 'top-center',
        autoClose: 5000,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
      }
    )
  }

  //get assets liabilities and deductions for creating google sheet
  const [assets, setAssets] = useState([])
  const [liabilities, setLiabilities] = useState([])
  const [deductions, setDeductions] = useState([])
  const [incomes, setIncomes] = useState([])
  const [expenses, setExpenses] = useState([])

  const dollarFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  const dollarCentsFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  })

  const getAccounts = useCallback(async () => {
    const {
      data: { items },
    } = await apiClient.get('api/assets')
    // console.log(items)
    const foundAssets =
      items?.filter(({ type }) => type === 'asset' || type === 'assetIncome') ||
      []
    setAssets(foundAssets)
    const foundLiabilities =
      items?.filter(({ type }) => type === 'liability') || []
    setLiabilities(foundLiabilities)
    const foundDeductions =
      items?.filter(({ type }) => type === 'deduction') || []
    setDeductions(foundDeductions)
    const foundIncomes =
      items?.filter(
        ({ type }) => type === 'income' || type === 'assetIncome'
      ) || []
    setIncomes(foundIncomes)
    const foundExpenses =
      items?.filter(({ type }) => type === 'expense' || type === 'liability') ||
      []
    setExpenses(foundExpenses)
  }, [])

  useEffect(() => {
    let fetched = false
    if (fetched) return

    getAccounts()
    fetched = true
  }, [getAccounts])

  const { valueOfAssets, valueOfInvestableAssets, readinessScoreAdjustedMax } =
    UpdatedCalcs({
      assets,
      liabilities,
    })

  const showUnion = deductions.some((obj) => obj.subType === 'uniondues')

  const plannedRetirementAge = Math.floor(
    differenceInMonths(
      new Date(intakeState.dateOfPlannedRetirement),
      new Date(intakeState.dateOfBirth)
    ) / 12
  )
  const [fegliOptionAgeRange, setFegliOptionAgeRange] =
    useState(plannedRetirementAge)
  const switchFegliOptionAgeRange = (event, newAgeRange) => {
    newAgeRange === null
      ? setFegliOptionAgeRange(plannedRetirementAge)
      : setFegliOptionAgeRange(newAgeRange)
  }
  const dateOfBirthDate = intakeState?.dateOfBirth
    ? new Date(intakeState.dateOfBirth)
    : new Date()
  const tspMaxContributionAmount = [1962, 1963, 1964, 1965].includes(
    dateOfBirthDate.getFullYear()
  )
    ? 34750
    : dateToYears(dateOfBirthDate, new Date()).years >= 50
    ? 31000
    : 23500

  const {
    minimumRetirementAge,
    firstFullyEligibleDate,
    atRetirementGrossPension,
    atRetirementFERSSupplement,
    retirementAgeLessThanMRA,
    monthOfExtraServiceSick,
    optimalSickLeave,
    survivorBenefitCost,
    survivorBenefitMonthlyPayout,
    formerSpouseOwedPensionPercentage,
  } = useDashboardCalculations(deductions, liabilities)

  const {
    cashAddbackTotals,
    adjustedNetTakeHomeInRetirement,
    adjustedWorkingNetTakeHome,
    adjustedNetTakeHomeDifferencePARCOGap,
  } = useRetirementGapCalculations(deductions, liabilities, incomes, expenses)

  const {
    basicLife,
    optionA,
    optionB,
    optionC,
    fegliTotal,
    privateInsurance,
    allLifeInsuranceTotal,
    totalEstate,
    grossCoverageNeeded,
  } = useFegliCalculations(fegliOptionAgeRange)

  const sendToGoogleSheets = () => {
    const {
      createdAt,
      updatedAt,
      recommendations,
      documents,
      ...submittableValues
    } = intakeState
    const tspAssets = [
      {
        name: 'TSP Traditional',
        subTypeClassification: '401(k)-tsptraditional',
        amount: intakeState?.tspBalance || 0,
      },
      {
        name: 'TSP Roth',
        subTypeClassification: '401(k)-tsproth',
        amount: intakeState?.tspRothBalance || 0,
      },
    ]
    const assetsWithTSP = [...tspAssets, ...assets]
    const quickSheetWithFinances = {
      ...intakeState,
      assets: assetsWithTSP,
      liabilities: liabilities,
      deductions: deductions,
      incomes: incomes,
      expenses: expenses,
    }
    callZapier({
      user: intakeState,
      hookUrl: 'https://hooks.zapier.com/hooks/catch/15607509/22mzznj/',
      payload: {
        user: quickSheetWithFinances,
      },
      sendNotification: false,
    })
    toast.success(
      'You have successfully started the `Create Client Output` Automation',
      {
        position: 'top-center',
        autoClose: 5000,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
      }
    )
  }
  // console.log('adminDetails', adminDetails)
  const sendWelcomeEmail = async () => {
    if (!intakeState?.email || !intakeState?.phone) {
      toast.error(
        `NO EMAIL SENT! Your user must have an email and phone number to send a welcome email`,
        {
          position: 'top-center',
          autoClose: 5000,
          pauseOnFocusLoss: false,
          pauseOnHover: false,
        }
      )
      return
    }
    const emailPayload = {
      email: intakeState?.email,
      phone: intakeState?.phone,
      parcoRep: adminDetails?.email || 'zburklund@goparco.com',
    }
    await apiRequest('/api/users/sendWelcomeEmail', {
      method: 'POST',
      body: JSON.stringify(emailPayload),
    })
    toast.success(
      `You have successfully sent a welcome email to ${intakeState?.firstName} ${intakeState?.lastName} at: ${intakeState?.email}`,
      {
        position: 'top-center',
        autoClose: 5000,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
      }
    )
  }
  // const sendToRetirementPaperwork = async () => {
  //   const dateOfBirthText = intakeState?.dateOfBirth
  //     ? format(new Date(intakeState.dateOfBirth), 'MM/dd/yyyy')
  //     : ''
  //   const dateOfPlannedRetirementText = intakeState?.dateOfPlannedRetirement
  //     ? format(new Date(intakeState.dateOfPlannedRetirement), 'MM/dd/yyyy')
  //     : ''
  //   const dateOfSpouseBirthText = intakeState?.familySpouseDateOfBirth
  //     ? format(new Date(intakeState.familySpouseDateOfBirth), 'MM/dd/yyyy')
  //     : ''
  //   const agencyLabel =
  //     selectData.agency.find(({ value }) => value === intakeState.agency)
  //       ?.label || ''
  //   const marriedText =
  //     intakeState?.familyMaritalStatus === 'married' ? 'X' : ''
  //   const unMarriedText =
  //     intakeState?.familyMaritalStatus !== 'married' ? 'X' : ''
  //   const armedForcesYes = intakeState?.military ? 'X' : ''
  //   const armedForcesNo = intakeState?.military ? '' : 'X'
  //   const retirementPaperwork = [
  //     'documentName=' +
  //       'Retirement-Paperwork-' +
  //       intakeState?.firstName +
  //       '-' +
  //       intakeState?.lastName +
  //       '.pdf',
  //     '1ANameLastFirstMiddle=' +
  //       intakeState?.lastName +
  //       ', ' +
  //       intakeState?.firstName,
  //     '1AAllOtherNames=',
  //     '1AAddressStreet=' + intakeState?.address,
  //     '1ACityStateZipcode=' +
  //       intakeState?.city +
  //       ', ' +
  //       intakeState?.state +
  //       ' ' +
  //       intakeState?.zipcode,
  //     '1ADaytimePhone=' + intakeState?.phone,
  //     '1ABestTime=',
  //     '1AEmail=' + intakeState?.email,
  //     '1ADOB=' + dateOfBirthText,
  //     '1ACitizen=X',
  //     '1BDateOfFinalSeparation=' + dateOfPlannedRetirementText,
  //     '1BAgency=' + agencyLabel,
  //     '1BTitleOfPosition=' + intakeState?.positionTitle,
  //     '1ADisability=X',
  //     '1CSpouseNameLastFirstMiddle=',
  //     '1CSpouseDOB=' + dateOfSpouseBirthText,
  //     '1BArmedForcesYes=' + armedForcesYes,
  //     '1BArmedForcesNo=' + armedForcesNo,
  //     '1BMilitaryRetiredPayNo=',
  //     '1BMilitaryRetiredPayYes=',
  //     '1CMarriedYes=' + marriedText,
  //     '1CMarriedNo=' + unMarriedText,
  //     '1CFormerSpouseAnnuityYes=',
  //     '1CFormerSpouseAnnuityNo=X',
  //     '2EFEHBYes=X',
  //     '2EFEHBNo=',
  //     '2ECourtYes=',
  //     '2ECourtNo=',
  //     '2EFEGLIYes=X',
  //     '2EFEGLINo=',
  //     '2EFDVYes=',
  //     '2EFDVNo=X',
  //     '2EFLTCYes=',
  //     '2EFLTCNo=X',
  //     '3HCheckingSavings=X',
  //     '3HDirectExpress=',
  //     '3HOutsideUS=',
  //     '3HTaxAnnuityYes=X',
  //     '3HTaxAnnuityNo=',
  //     '3HTaxSalaryYes=X',
  //     '3HTaxSalaryNo=',
  //     '4NameLastFirstMiddle=' +
  //       intakeState?.lastName +
  //       ', ' +
  //       intakeState?.firstName,
  //     '4DOB=' + dateOfBirthText,
  //     '4AMilitaryDepositYes=',
  //     '4AMilitaryDepositNo=X',
  //     '4CWorkersCompYes=X',
  //     '4COPMYes=X',
  //     '6EServiceComplete=X',
  //     '71NameLastFirstMiddle=' +
  //       intakeState?.lastName +
  //       ', ' +
  //       intakeState?.firstName,
  //     '71DOB=' + dateOfBirthText,
  //     '72NameFirstLast=' + intakeState?.firstName + ' ' + intakeState?.lastName,
  //     '8NameLastFirstMiddle=' +
  //       intakeState?.lastName +
  //       ', ' +
  //       intakeState?.firstName,
  //     '8DOB=' + dateOfBirthText,
  //     '8Agency=' + agencyLabel,
  //     '8BasicLifeYes=X',
  //     '8BasicLifeNo=',
  //     '8BasicLifeLB=',
  //     '8Reduction75=',
  //     '8OptionAYes=',
  //     '8OptionANo=',
  //     '8OptionANA=',
  //     '8OptionBYes=',
  //     '8OptionBNo=',
  //     '8OptionBNA=X',
  //     '8OptionBNoReductionMultiples=',
  //     '8OptionBFullReductionMultiples=',
  //     '8OptionCYes=',
  //     '8OptionCNo=',
  //     '8OptionCNA=X',
  //     '8OptionCNoReductionMultiples=',
  //     '8OptionCFullReductionMultiples=',
  //   ]
  //   const payload = {
  //     script: 'retirementPaperwork/pdfMapper.py',
  //     args: retirementPaperwork,
  //   }
  //   try {
  //     const response = await fetch(
  //       'https://evident-actually-tahr.ngrok-free.app/run-script',
  //       {
  //         method: 'POST',
  //         headers: {
  //           'Content-Type': 'application/json',
  //         },
  //         body: JSON.stringify(payload),
  //       }
  //     )
  //   } catch (e) {
  //     console.log(e)
  //   }
  //   // callZapier({
  //   //   user: intakeState,
  //   //   hookUrl: 'https://evident-actually-tahr.ngrok-free.app/run-script',
  //   //   payload: {
  //   //     script: 'retirementPaperwork/pdfMapper.py',
  //   //     args: retirementPaperwork,
  //   //   },
  //   //   sendNotification: false,
  //   // })
  //   toast.success('You have successfully created Retirement Paperwork', {
  //     position: 'top-center',
  //     autoClose: 5000,
  //     pauseOnFocusLoss: false,
  //     pauseOnHover: false,
  //   })
  // }
  const [id, setId] = useState()
  const [version, setVersion] = useState()
  const [type, setType] = useState()
  const [name, setName] = useState()
  const [plaidName, setPlaidName] = useState()
  const [subType, setSubType] = useState()
  const [sortKey, setSortKey] = useState()
  const [amount, setAmount] = useState()
  const [subTypeClassification, setSubTypeClassification] = useState()
  const [interestRate, setInterestRate] = useState()
  const [monthlyPayment, setMonthlyPayment] = useState()
  const [frequency, setFrequency] = useState()
  const [loanEndDate, setLoanEndDate] = useState()
  const [modalTitle, setModalTitle] = useState()
  const [open, setOpen] = useState(false)
  const handleViewAsset = ({
    id,
    typeSubTypeUuid,
    _version,
    name,
    plaidName,
    amount,
    subType,
    subTypeClassification,
  }) => {
    setType('asset')
    setId(id)
    setVersion(_version)
    setSortKey(typeSubTypeUuid)
    setName(name)
    setPlaidName(plaidName)
    setSubType(subType)
    setAmount(amount)
    setOpen(true)
    setModalTitle(`Update ${name}`)
    setSubTypeClassification(subTypeClassification)
  }

  const handleViewLiability = ({
    id,
    typeSubTypeUuid,
    _version,
    name,
    plaidName,
    amount,
    subType,
    subTypeClassification,
    interestRate,
    loanEndDate,
    monthlyPayment,
  }) => {
    setType('liability')
    setId(id)
    setVersion(_version)
    setSortKey(typeSubTypeUuid)
    setName(name)
    setPlaidName(plaidName)
    setSubType(subType)
    setAmount(amount)
    setOpen(true)
    setModalTitle(`Update ${name}`)
    setSubTypeClassification(subTypeClassification)
    setInterestRate(interestRate)
    setLoanEndDate(loanEndDate)
    setMonthlyPayment(monthlyPayment)
  }

  const handleViewIncome = ({
    id,
    typeSubTypeUuid,
    _version,
    name,
    plaidName,
    amount,
    subType,
    subTypeClassification,
    interestRate,
    loanEndDate,
    monthlyPayment,
    frequency,
  }) => {
    setType('income')
    setId(id)
    setVersion(_version)
    setSortKey(typeSubTypeUuid)
    setName(name)
    setPlaidName(plaidName)
    setSubType(subType)
    setAmount(amount)
    setOpen(true)
    setModalTitle(`Update ${name}`)
    setSubTypeClassification(subTypeClassification)
    setInterestRate(interestRate)
    setLoanEndDate(loanEndDate)
    setMonthlyPayment(monthlyPayment)
    setFrequency(frequency)
  }

  const handleAssetClickOpen = () => {
    setOpen(true)
    // setSubType('')
    setType('asset')
    setModalTitle('Add An Asset')
  }

  const handleIncomeClickOpen = () => {
    setOpen(true)
    setSubType('')
    setType('income')
    setModalTitle('Add an Income Stream')
  }

  const handleExpenseClickOpen = () => {
    setOpen(true)
    setSubType('')
    setType('expense')
    setModalTitle('Add an Expense')
  }

  const handleSubmit = () => {
    handleClose()
  }

  const handleClose = () => {
    setOpen(false)
    setVersion('')
    setId('')
    setType('')
    setName('')
    setPlaidName('')
    setSubType('')
    setAmount('')
    setSortKey('')
    setModalTitle('')
    setSubTypeClassification('')
    setInterestRate('')
    setLoanEndDate('')
    setMonthlyPayment('')
    setFrequency('')
  }

  const assetSum =
    (assets || []).map((assets) => assets.amount).reduce((a, b) => a + b, 0) +
    (intakeState.tspBalance ? intakeState.tspBalance : 0) +
    (intakeState.tspRothBalance ? intakeState.tspRothBalance : 0)

  const liabilitySum = (liabilities || [])
    .map((liabilities) => liabilities.amount)
    .reduce((a, b) => a + b, 0)

  const netWorth = assetSum - liabilitySum

  const [anchorEl, setAnchorEl] = useState(null)
  const tableMenuOpen = Boolean(anchorEl)
  const handleTableMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleTableMenuClose = () => {
    setAnchorEl(null)
  }
  const [tableStates, setTableStates] = useState({
    values: false,
    expand: false,
    paycheck: false,
    working: false,
    difference: false,
  })
  const handleTableStateChange = (event) => {
    setTableStates({
      ...tableStates,
      [event.target.name]: event.target.checked,
    })
  }

  const totalEstateWithNetWorth = totalEstate + netWorth

  const legacyGap = isNaN(totalEstateWithNetWorth - grossCoverageNeeded)
    ? 0
    : totalEstateWithNetWorth - grossCoverageNeeded
  const legacyScore = isNaN(
    Math.round((totalEstateWithNetWorth / grossCoverageNeeded) * 100)
  )
    ? 0
    : Math.round((totalEstateWithNetWorth / grossCoverageNeeded) * 100)
  const legacyScoreMax = Math.min(legacyScore, 100)

  const [collapseSections, setCollapseSections] = useState({
    personal: true,
    employment: true,
    military: true,
    family: true,
    leaveAndEarnings: true,
    tspContributions: true,
    healthcare: true,
    deductions: true,
    fegli: true,
    union: true,
    survivorBenefit: true,
    balanceSheet: true,
    tspAllocation: true,
    goals: true,
    socialSecurity: true,
    taxes: true,
    budgeting: true,
    incomeStatement: true,
    todo: true,
    schedule: true,
    other: true,
    financialframeworkcreator: true,
    annuityapplicationcreator: true,
    productbank: true,
    '401kContributions': true,
  })
  const toggleCollapse = (section) => {
    setCollapseSections((prevState) => ({
      ...prevState,
      [section]: !prevState[section],
    }))
  }

  useAutoUpdateScores({
    intakeState,
    updateIntakeStateAndSubmit,
    updateIntakeStateAtKey,
    assets,
    liabilities,
    deductions,
  })

  return (
    <DashboardLayout>
      <BalanceSheetModalForm
        handleClose={handleClose}
        type={type}
        name={name}
        amount={amount}
        version={version}
        id={id}
        subType={subType}
        setVersion={setVersion}
        setId={setId}
        setType={setType}
        setSubType={setSubType}
        setSubTypeClassification={setSubTypeClassification}
        subTypeClassification={subTypeClassification}
        interestRate={interestRate}
        setInterestRate={setInterestRate}
        monthlyPayment={monthlyPayment}
        setMonthlyPayment={setMonthlyPayment}
        loanEndDate={loanEndDate}
        setLoanEndDate={setLoanEndDate}
        frequency={frequency}
        setFrequency={setFrequency}
        setName={setName}
        plaidName={plaidName}
        setAmount={setAmount}
        typeSubTypeUuid={sortKey}
        key={sortKey}
        open={open}
        handleSubmit={handleSubmit}
        reloadAccounts={getAccounts}
        modalHeader={modalTitle}
      />
      <Box
        px={{
          xs: 4,
          sm: 4,
          md: 4,
          lg: 6,
          xl: 6,
          xxl: 6,
          test: 6,
          xl3: 6,
        }}
      >
        <Grid item xs={12} mb={1} mt={4}>
          <Typography
            variant="h1"
            fontWeight="600"
            fontSize="28px"
            textAlign="left"
            color="#262627"
          >
            Admin Inputs - {userNameTitle}
          </Typography>
        </Grid>
      </Box>
      <Grid container spacing={2} p={2}>
        <Box
          sx={{
            position: 'fixed',
            top: '10px',
            right: 16,
            padding: '5px',
            backgroundColor: '#fff',
            boxShadow: '0px 4px 25px -8px rgba(0,0,0,0.2)',
            borderRadius: '8px',
            zIndex: 1,
          }}
        >
          <LoadingButton
            loading={loading}
            onClick={onSubmit}
            sx={{ padding: '8px 24px' }}
          />
        </Box>
        <Grid item xs={12} md={4} xl={3} sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'relative',
              width: {
                xs: 'calc(100% - 32px)',
                md: 'calc((100% - 66px) / 3)',
                lg: 'calc((100% - 363px) / 3)',
                xl: 'calc((100% - 378px) / 4)',
              },
            }}
          >
            <QuickNav
              showUnion={showUnion}
              showSurvivorBenefit={showSurvivorBenefit}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={8} xl={9}></Grid>

        <Grid item xs={12} md={4} xl={3} sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'relative',
              width: {
                xs: 'calc(100% - 32px)',
                md: 'calc((100% - 66px) / 3)',
                lg: 'calc((100% - 363px) / 3)',
                xl: 'calc((100% - 378px) / 4)',
              },
            }}
          >
            <StickyNotes
              intakeState={intakeState}
              updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
              updateIntakeStateAtKey={updateIntakeStateAtKey}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={8} xl={9} pt={0}></Grid>
        <Grid item xs={12} md={4} xl={3}></Grid>
        <Grid item xs={12} md={8} xl={9}>
          <QuickReference
            sendToGoogleSheets={sendToGoogleSheets}
            sendToSalesforce={sendToSalesforce}
            updateSalesforce={updateSalesforce}
            sendICToSalesforce={sendICToSalesforce}
            sendWelcomeEmail={sendWelcomeEmail}
            // sendToRetirementPaperwork={sendToRetirementPaperwork}
          />
        </Grid>

        <BasicInfo
          collapseSections={collapseSections}
          toggleCollapse={() => toggleCollapse('personal')}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        <Employment
          collapseSections={collapseSections}
          toggleCollapse={() => toggleCollapse('employment')}
          firstFullyEligibleDate={firstFullyEligibleDate}
          atRetirementFERSSupplement={atRetirementFERSSupplement}
          // moved this into the employment section instead of loading it here
          // creditableYearsOfServiceAtRetirement={
          //   creditableYearsOfServiceAtRetirement
          // }
          // creditableYearsOfServiceToday={creditableYearsOfServiceToday}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        <Military
          collapseSections={collapseSections}
          toggleCollapse={() => toggleCollapse('military')}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        <Family
          collapseSections={collapseSections}
          toggleCollapse={() => toggleCollapse('family')}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
          formerSpouseOwedPensionPercentage={formerSpouseOwedPensionPercentage}
          atRetirementGrossPension={atRetirementGrossPension}
        />

        {isGovernmentEmployee && (
          <LeaveAndEarnings
            collapseSections={collapseSections}
            monthOfExtraServiceSick={monthOfExtraServiceSick}
            optimalSickLeave={optimalSickLeave}
            toggleCollapse={() => toggleCollapse('leaveAndEarnings')}
            intakeState={intakeState}
            updateIntakeStateAtKey={updateIntakeStateAtKey}
            updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
          />
        )}

        {isGovernmentEmployee ? (
          <TSPContributions
            collapseSections={collapseSections}
            tspMaxContributionAmount={tspMaxContributionAmount}
            toggleCollapse={() => {
              toggleCollapse('tspContributions')
            }}
            intakeState={intakeState}
            updateIntakeStateAtKey={updateIntakeStateAtKey}
            updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
          />
        ) : (
          <Contributions401k
            collapseSections={collapseSections}
            toggleCollapse={() => {
              toggleCollapse('401kContributions')
            }}
            intakeState={intakeState}
            updateIntakeStateAtKey={updateIntakeStateAtKey}
            updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
          />
        )}
        <Healthcare
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('healthcare')
          }}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />
        <Deductions
          deductions={deductions}
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('deductions')
          }}
          cashAddbackTotals={cashAddbackTotals}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        <Fegli
          legacyScore={legacyScore}
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('fegli')
          }}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        {showUnion && (
          <>
            <Union
              collapseSections={collapseSections}
              toggleCollapse={() => {
                toggleCollapse('union')
              }}
              intakeState={intakeState}
              updateIntakeStateAtKey={updateIntakeStateAtKey}
              updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
            />
          </>
        )}
        {showSurvivorBenefit && isGovernmentEmployee && (
          <>
            <SurvivorBenefit
              collapseSections={collapseSections}
              toggleCollapse={() => {
                toggleCollapse('survivorBenefit')
              }}
              survivorBenefitMonthlyPayout={survivorBenefitMonthlyPayout}
              survivorBenefitCost={survivorBenefitCost}
              intakeState={intakeState}
              updateIntakeStateAtKey={updateIntakeStateAtKey}
              updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
            />
          </>
        )}

        <BalanceSheet
          assets={assets}
          liabilities={liabilities}
          collapseSections={collapseSections}
          getAccounts={getAccounts}
          toggleCollapse={() => {
            toggleCollapse('balanceSheet')
          }}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
          isAdmin={isAdmin}
        />
        {isGovernmentEmployee && (
          <TSPAllocation
            collapseSections={collapseSections}
            toggleCollapse={() => {
              toggleCollapse('tspAllocation')
            }}
            intakeState={intakeState}
            updateIntakeStateAtKey={updateIntakeStateAtKey}
            updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
          />
        )}

        <Goals
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('goals')
          }}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        <SocialSecurity
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('socialSecurity')
          }}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        <Taxes
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('taxes')
          }}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        <Budgeting
          incomes={incomes}
          expenses={expenses}
          getAccounts={getAccounts}
          intakeState={intakeState}
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('budgeting')
          }}
        />

        <IncomeStatement
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('incomeStatement')
          }}
          adjustedNetTakeHomeDifferencePARCOGap={
            adjustedNetTakeHomeDifferencePARCOGap
          }
          deductions={deductions}
          liabilities={liabilities}
          incomes={incomes}
          expenses={expenses}
          readinessScoreAdjustedMax={readinessScoreAdjustedMax}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
          getAccounts={getAccounts}
        />

        <ToDoList
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('todo')
          }}
          userTasks={userTasks}
          assets={assets}
        />

        <AdminScheduleAppointment
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('schedule')
          }}
        />

        <Other
          valueOfInvestableAssets={valueOfInvestableAssets}
          fegliTotal={fegliTotal}
          optionC={optionC}
          adjustedNetTakeHomeInRetirement={adjustedNetTakeHomeInRetirement}
          adjustedWorkingNetTakeHome={adjustedWorkingNetTakeHome}
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('other')
          }}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        <FinancialFrameworkCreator
          valueOfInvestableAssets={valueOfInvestableAssets}
          adjustedNetTakeHomeDifferencePARCOGap={
            adjustedNetTakeHomeDifferencePARCOGap
          }
          adjustedWorkingNetTakeHome={adjustedWorkingNetTakeHome}
          adjustedNetTakeHomeInRetirement={adjustedNetTakeHomeInRetirement}
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('financialframeworkcreator')
          }}
          atRetirementFERSSupplement={atRetirementFERSSupplement}
          retirementAgeLessThanMRA={retirementAgeLessThanMRA}
          intakeState={intakeState}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
          retirementLifestyleGoal={
            intakeState?.retirementLifestyleGoal?.lifestyleGoal || 1
          }
        />

        <AnnuityApplicationCreator
          valueOfInvestableAssets={valueOfInvestableAssets}
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('annuityapplicationcreator')
          }}
          intakeState={intakeState}
          netWorth={netWorth}
          assets={assets}
          liabilities={liabilities}
          updateIntakeStateAtKey={updateIntakeStateAtKey}
          updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
        />

        <ProductBank
          collapseSections={collapseSections}
          toggleCollapse={() => {
            toggleCollapse('productbank')
          }}
          intakeState={intakeState}
        />
      </Grid>
    </DashboardLayout>
  )
}

export default MasterInputs
